import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Button,
  Switch,
  Upload,
  Alert,
} from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import { InboxOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { TYPES_DEPENSE, formatMontant } from '../../variables/constants';
import CBModal from '../../components/CBModal';
import { useTranslation, Trans } from 'react-i18next';
const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const GREEN = '#1a6b3c';

/**
 * Ajouter une facture au dossier, ou en corriger une (`facture` fourni).
 *
 * En correction, seule la facture elle-même se modifie - nature, prestataire, date,
 * numéro, montant réclamé. La décision (retenu, motif) reste l'affaire de « Traiter » :
 * les deux gestes ne se mélangent pas, et changer le montant d'une facture déjà
 * traitée la remet en attente côté serveur.
 */
export default function NouvelleFactureModal({ open, onClose, dossierId, onCreated, facture = null }) {
  const { t } = useTranslation('reclamations');
  const edition = !!facture;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fichier, setFichier] = useState(null);

  const montantReclame = Form.useWatch('montantReclame', form);
  const montantRetenu = Form.useWatch('montantRetenu', form);
  const validationPartielle = montantRetenu != null && montantReclame != null && montantRetenu < montantReclame;

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(facture ? {
        typeDepense: facture.typeDepense,
        nomPrestataire: facture.nomPrestataire,
        numeroFactureOriginal: facture.numeroFactureOriginal,
        dateFacture: facture.dateFacture ? dayjs(facture.dateFacture) : null,
        // 0 = « À compléter » : champ vide plutôt qu'un zéro à effacer.
        montantReclame: Number(facture.montantReclame) > 0 ? Number(facture.montantReclame) : undefined,
      } : {
        dateFacture: dayjs(),
        lienAvecAccidentVerifie: false,
      });
      setFichier(null);
    }
  }, [open, form, facture]);

  const enregistrerCorrection = async (vals) => {
    setLoading(true);
    try {
      const res = await axiosInstance.put(`/v1/factures-reclamation/${facture.factureTrackingId}`, {
        typeDepense: vals.typeDepense,
        nomPrestataire: vals.nomPrestataire?.trim(),
        numeroFactureOriginal: vals.numeroFactureOriginal?.trim() || null,
        dateFacture: vals.dateFacture?.format('YYYY-MM-DD'),
        montantReclame: vals.montantReclame,
      });
      if (fichier) {
        const fd = new FormData();
        fd.append('file', fichier);
        fd.append('titre', `Facture ${vals.nomPrestataire?.trim()} - ${vals.numeroFactureOriginal?.trim() || ''}`);
        fd.append('typeDocument', 'FACTURE_RECLAMATION');
        try {
          await axiosInstance.post(`/v1/factures-reclamation/${facture.factureTrackingId}/document`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          toast.warning(t('facture.messages.modifieeEchecScan'));
        }
      }
      const message = res.data?.message || t('facture.messages.modifiee');
      if (/attente/i.test(message)) toast.info(message); else toast.success(message);
      onCreated?.(res.data?.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('facture.messages.erreurModification'));
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (vals) => {
    if (edition) {
      await enregistrerCorrection(vals);
      return;
    }
    if (!dossierId) {
      toast.error(t('facture.messages.dossierInvalide'));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        typeDepense: vals.typeDepense,
        nomPrestataire: vals.nomPrestataire?.trim(),
        numeroFactureOriginal: vals.numeroFactureOriginal?.trim() || null,
        dateFacture: vals.dateFacture?.format('YYYY-MM-DD'),
        montantReclame: vals.montantReclame,
        montantRetenu: vals.montantRetenu ?? null,
        lienAvecAccidentVerifie: !!vals.lienAvecAccidentVerifie,
        motifRejet: vals.motifRejet?.trim() || null,
      };

      const res = await axiosInstance.post(`/v1/dossiers-reclamation/${dossierId}/factures`, payload);
      const facture = res.data?.data;
      const factureId = facture?.factureTrackingId;

      if (fichier && factureId) {
        const fd = new FormData();
        fd.append('file', fichier);
        fd.append('titre', `Facture ${payload.nomPrestataire} - ${payload.numeroFactureOriginal || ''}`);
        fd.append('typeDocument', 'FACTURE_RECLAMATION');
        try {
          await axiosInstance.post(`/v1/factures-reclamation/${factureId}/document`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          toast.warning(t('facture.messages.enregistreeEchecScan'));
        }
      }

      toast.success(t('facture.messages.ajoutee'));
      onCreated?.(facture);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('facture.messages.erreurAjout'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CBModal
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <DollarOutlined style={{ color: GREEN, marginRight: 8 }} />
            {edition ? t('facture.titreModification') : t('facture.titreAjout')}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
            {edition ? t('facture.sousTitreModification') : t('facture.sousTitreAjout')}
          </div>
        </div>
      }
      open={open} onCancel={onClose} footer={null} width={820} destroyOnClose
      styles={{ body: { paddingTop: 16, maxHeight: '75vh', overflowY: 'auto' } }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {edition && facture.statutTraitement && facture.statutTraitement !== 'EN_ATTENTE' && (
          <Alert
            showIcon
            type="warning"
            style={{ borderRadius: 10, marginBottom: 16 }}
            message={t('facture.dejaTraitee')}
            description={t('facture.dejaTraiteeAide')}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item
              name="typeDepense"
              label={t('facture.champs.nature')}
              rules={[{ required: true, message: t('facture.champs.natureRequise') }]}
            >
              <Select placeholder={t('facture.champs.selectionner')}>
                {TYPES_DEPENSE.map(t => (
                  <Option key={t.value} value={t.value}>
                    <span style={{ marginRight: 8 }}>{t.icon}</span>
                    {t.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={14}>
            <Form.Item
              name="nomPrestataire"
              label={t('facture.champs.prestataire')}
              rules={[{ required: true, message: t('facture.champs.prestataireRequis') }]}
            >
              <Input placeholder={t('facture.champs.prestatairePlaceholder')} maxLength={200} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item name="numeroFactureOriginal" label={t('facture.champs.numeroOrigine')}>
              <Input placeholder={t('facture.champs.numeroOriginePlaceholder')} maxLength={80} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="dateFacture"
              label={t('facture.champs.date')}
              rules={[{ required: true, message: t('facture.champs.dateRequise') }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('facture.champs.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </Col>
          {!edition && (
            <Col xs={24} md={6}>
              <Form.Item
                name="lienAvecAccidentVerifie"
                label={t('facture.champs.lienVerifie')}
                valuePropName="checked"
                tooltip={t('facture.champs.lienVerifieAide')}
              >
                <Switch checkedChildren={t('facture.champs.oui')} unCheckedChildren={t('facture.champs.non')} />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="montantReclame"
              label={t('commun.montantReclameFcfa')}
              rules={[
                { required: true, message: t('facture.champs.montantReclameRequis') },
                { type: 'number', min: 1, message: t('facture.champs.montantPositif') },
              ]}
            >
              <CBInputNumber
                style={{ width: '100%' }}
                min={0}
                step={500}
                placeholder={t('facture.champs.montantReclamePlaceholder')}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(v) => v?.replace(/\s/g, '')}
              />
            </Form.Item>
          </Col>
          {!edition && (
            <Col xs={24} md={12}>
              <Form.Item
                name="montantRetenu"
                label={t('commun.montantRetenuFcfa')}
                tooltip={t('facture.champs.montantRetenuAide')}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value != null && montantReclame != null && value > montantReclame) {
                        return Promise.reject(new Error(t('facture.champs.montantRetenuExcede')));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <CBInputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={500}
                  placeholder={t('facture.champs.montantRetenuPlaceholder')}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(v) => v?.replace(/\s/g, '')}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {!edition && validationPartielle && (
          <Alert
            showIcon
            type="warning"
            style={{ borderRadius: 10, marginBottom: 16 }}
            message={
              <span>
                {t('facture.validationPartielle', { montant: formatMontant(montantReclame - montantRetenu) })}
              </span>
            }
            description={t('facture.validationPartielleAide')}
          />
        )}

        {!edition && (validationPartielle || montantRetenu === 0) && (
          <Form.Item
            name="motifRejet"
            label={montantRetenu === 0 ? t('commun.motifRejet') : t('commun.motifReduction')}
            rules={[{ required: true, message: t('commun.motifRequis') }]}
          >
            <TextArea rows={2} placeholder={t('commun.motifPlaceholder')} maxLength={500} />
          </Form.Item>
        )}

        {/* En correction, le scan ne se propose que s'il manque : en ajouter un
            second créerait un doublon dans la GED. */}
        {!(edition && facture.ossanGedDocumentId) && (
          <Form.Item label={t('facture.scan.label')} style={{ marginTop: 8 }}>
            <Dragger
              name="file"
              multiple={false}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png"
              beforeUpload={(file) => {
                if (file.size / 1024 / 1024 >= 20) {
                  toast.error(t('facture.scan.tropGros'));
                  return Upload.LIST_IGNORE;
                }
                setFichier(file);
                return false;
              }}
              onRemove={() => { setFichier(null); return true; }}
              style={{ background: '#fafafa' }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: GREEN }} /></p>
              <p className="ant-upload-text" style={{ fontSize: 13 }}>
                {t('facture.scan.glisser')}
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11 }}>
                <Trans t={t} i18nKey="facture.scan.formats" components={{ 1: <strong /> }} />
              </p>
            </Dragger>
          </Form.Item>
        )}

        <div style={{
          borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button onClick={onClose} disabled={loading}>{t('commun.annuler')}</Button>
          <Button
            type="primary" htmlType="submit" loading={loading}
            style={{ background: GREEN, borderColor: GREEN, minWidth: 140 }}
          >
            {edition ? t('facture.enregistrer') : t('facture.ajouter')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
